import { Component } from '@angular/core';
import { ModalController, NavParams, PickerController } from '@ionic/angular';
import { PickerOptions } from '@ionic/core';
import { TranslateService } from '@ngx-translate/core';
import { LazyLoadingSelectComponent } from '../../lazy-loading-select/lazy-loading-select.component';
import { ClassificationFormSelectComponent } from '../../classification-form-select/classification-form-select.component';
import { NewPageSelectComponent } from '../../new-page-select/new-page-select.component';
import { CapaManagementSystemCodeService } from '../../../../capa-management/capa-management-system-code.service';
import { DataService, SystemCodeService } from '../../../../service';

@Component({
  selector: 'app-findings-record-filter-modal',
  templateUrl: './findings-record-filter-modal.component.html',
  styleUrls: ['./findings-record-filter-modal.component.scss'],
  providers: [],
})
export class FindingsRecordFilterModalComponent {
  query: any = {};
  personInCharge: any = null;
  reviewer: any = null;
  verifier: any = null;
  currentExecutor: any = null;
  capaSourceLabel = '';

  statusList: any[] = [];
  completionStatusList: any[] = [];
  isFindingStatusMultiSelect = false;
  mainProcessList: any[] = [];

  constructor(
    public navParams: NavParams,
    public modalController: ModalController,
    public systemCodeService: CapaManagementSystemCodeService,
    public pickerController: PickerController,
    private translateService: TranslateService,
    private dataService: DataService,
    private systemCode: SystemCodeService,
  ) {
    this.query = this.navParams.data.query || {};
    this.personInCharge = this.query.personInCharge || null;
    this.reviewer = this.query.reviewer || null;
    this.verifier = this.query.verifier || null;
    this.currentExecutor = this.query.currentExecutor || null;
    this.capaSourceLabel = this.query.capaSourceLabel || '';
    this.isFindingStatusMultiSelect = this.navParams.data.isFindingStatusMultiSelect === true;

    this.systemCodeService.findingStatusList$.subscribe(list => this.statusList = list);
    this.systemCodeService.mainProcessList$.subscribe(list => this.mainProcessList = list);
    this.systemCodeService.completionStatusList$.subscribe(list => this.completionStatusList = list)
  }

  async selectStatus() {
    if (this.isFindingStatusMultiSelect) {
      return this.openMultiStatusModal(this.statusList);
    }
    return this.selectCode('status', this.statusList, 'status');
  }
  async selectCompletionStatus(){
    return this.selectCode('completionStatus',this.completionStatusList,'completionStatus');
  }
  private async openMultiStatusModal(statusList: any[]) {
    const lang = localStorage.getItem('APP_LANG');
    const displayFieldMap: any = {
      zh_CN: 'code_desc_zh',
      en_US: 'code_desc_en',
      zh_TW: 'code_desc_tr',
      vi: 'code_desc_vi',
      km: 'code_desc_km',
      th: 'code_desc_th',
      lo: 'code_desc_lo',
    };
    const displayField = displayFieldMap[lang] || 'code_desc_en';

    history.pushState(null, null, document.URL);
    const statusModal = await this.modalController.create({
      component: NewPageSelectComponent,
      componentProps: {
        pageTitle: this.translateService.instant('shared_components.common.status'),
        placeholderMessage: this.translateService.instant('shared_components.common.status'),
        optionList: statusList,
        displayField: displayField,
        valueField: 'code',
        multiSelect: true,
        needFilterSearch: false,
        cancelText: 'common.cancel',
        confirmText: 'common.confirm',
        selected: this.query.status ? String(this.query.status).split(',').filter(Boolean) : [],
      },
      showBackdrop: true,
      backdropDismiss: true,
    });
    statusModal.onDidDismiss().then(res => {
      if (Array.isArray(res.data)) {
        this.query.status = res.data.length > 0 ? res.data.join(',') : null;
      }
    });
    return statusModal.present();
  }

  getStatusLabel(status: any): string {
    if (!status) {
      return '';
    }
    return String(status).split(',').filter(Boolean)
      .map(code => this.systemCode.translate(code))
      .filter(Boolean)
      .join(', ');
  }

  async selectMainProcess() {
    return this.selectCode('mainProcess', this.mainProcessList, 'mainProcess');
  }

  async selectCapaSource() {
    this.dataService.capaSourceList(null).subscribe(async res => {
      const list: any[] = (res && res.DATA && res.DATA.list) || (res && res.DATA) || [];
      const categories = ['_SYS_CAPA_SC_1', '_SYS_CAPA_SC_2'];
      const map: { [key: string]: any[] } = {};
      categories.forEach(c => { map[c] = []; });
      list.forEach(src => {
        if (src && map[src.sourceCategory]) {
          map[src.sourceCategory].push(src);
        }
      });

      const optionList = categories
        .filter(c => map[c].length > 0)
        .map(c => ({
          label: this.systemCode.translate(c),
          value: c,
          code: c,
          childSpace: map[c].map(src => ({
            label: '[' + src.sourceCode + '] ' + src.sourceName,
            value: src.id,
            code: src.sourceCode,
            sourceCategory: c,
            childSpace: [],
          })),
        }));

      if (optionList.length === 0) {
        return;
      }

      history.pushState(null, null, document.URL);
      const modal = await this.modalController.create({
        component: ClassificationFormSelectComponent,
        componentProps: {
          pageTitle: 'shared_components.filter_modals.select_capa_source',
          placeholderMessage: 'shared_components.filter_modals.search_capa_source',
          optionList: optionList,
          selected: this.query.capaSourceId ? { value: this.query.capaSourceId } : null,
          returnSpaceTree: false,
        },
        showBackdrop: true,
        backdropDismiss: true,
      });
      modal.onDidDismiss().then(dismissRes => {
        if (dismissRes.data) {
          this.query.capaSourceId = dismissRes.data.value;
          this.query.capaSourceLabel = dismissRes.data.label;
          this.capaSourceLabel = dismissRes.data.label;
        }
      });
      await modal.present();
    });
  }

  private async selectCode(queryField: string, list: any[], columnName: string) {
    const optionsList = list || [];
    let selectedIndex = 0;
    if (this.query[queryField]) {
      const index = optionsList.findIndex(item => item.code === this.query[queryField]);
      selectedIndex = index >= 0 ? index : 0;
    }

    const options: PickerOptions = {
      buttons: [
        {
          text: this.translateService.instant('common.cancel'),
          role: 'cancel',
        },
        {
          text: this.translateService.instant('common.confirm'),
          handler: (value: any) => {
            this.query[queryField] = value[columnName].value;
          },
        },
      ],
      columns: [
        {
          name: columnName,
          options: this.getOptionList(optionsList),
          selectedIndex,
        },
      ],
    };

    const picker = await this.pickerController.create(options);
    return picker.present();
  }

  private getOptionList(list: any[]) {
    return list.map(item => ({
      text: this.getOptionText(item),
      value: item.code,
    }));
  }

  private getOptionText(item: any): string {
    const fieldMap: any = {
      zh_CN: 'code_desc_zh',
      en_US: 'code_desc_en',
      zh_TW: 'code_desc_tr',
      vi: 'code_desc_vi',
      km: 'code_desc_km',
      th: 'code_desc_th',
      lo: 'code_desc_lo',
    };
    const lang = localStorage.getItem('APP_LANG');
    const field = fieldMap[lang] || 'code_desc_en';
    return item[field] || item.name || item.code_desc_zh || item.code_desc_en || item.code;
  }

  async selectEmployee(field: 'chargePersonId' | 'reviewerId' | 'verifierId' | 'currentExecutorId', title: string) {
    const selectedMap = {
      chargePersonId: this.personInCharge,
      reviewerId: this.reviewer,
      verifierId: this.verifier,
      currentExecutorId: this.currentExecutor,
    };
    history.pushState(null, null, document.URL);
    const modal = await this.modalController.create({
      component: LazyLoadingSelectComponent,
      componentProps: {
        pageTitle: this.translateService.instant(title),
        placeholderMessage: 'shared_components.filter_modals.search_employee',
        displayField: ['code', 'full_name'],
        valueField: '$self',
        format: '[$1] $2',
        multiSelect: false,
        isStaff: true,
        selected: selectedMap[field] || '',
        cancelText: 'common.cancel',
        confirmText: 'common.confirm',
      },
      showBackdrop: true,
      backdropDismiss: true,
    });
    modal.onDidDismiss().then(res => {
      if (res.data) {
        if (field === 'chargePersonId') {
          this.personInCharge = res.data;
          this.query.personInCharge = res.data;
        } else if (field === 'reviewerId') {
          this.reviewer = res.data;
          this.query.reviewer = res.data;
        } else if (field === 'verifierId') {
          this.verifier = res.data;
          this.query.verifier = res.data;
        } else {
          this.currentExecutor = res.data;
          this.query.currentExecutor = res.data;
        }
        this.query[field] = res.data.id;
      }
    });
    await modal.present();
  }

  getMainProcessName(code: string): string {
    const found = this.mainProcessList.find(p => p.code === code);
    return found ? this.getOptionText(found) : code;
  }

  reset() {
    this.query = {};
    this.personInCharge = null;
    this.reviewer = null;
    this.verifier = null;
    this.currentExecutor = null;
    this.capaSourceLabel = '';
  }

  cancel() {
    this.modalController.dismiss();
  }

  confirm() {
    this.modalController.dismiss({ query: this.query });
  }

  displayEmployee(employee: any): string {
    if (!employee) {
      return '';
    }
    if (employee.full_name) {
      return employee.code ? '[' + employee.code + '] ' + employee.full_name : employee.full_name;
    }
    return employee.name || employee.code || '';
  }
}
